import { createCipheriv, createDecipheriv, randomBytes } from 'crypto';

// --------------------------------
// Types
// --------------------------------
export interface AesGcmResult {
  iv: Buffer;
  cipher: Buffer;
  tag: Buffer;
}

// --------------------------------
// Internal helpers
// --------------------------------
const KEY_32_BUFF = Buffer.from('we3x4hnky5qh3t6ds47c16hpd34c5rm4', 'utf8');

const toBuf = (u8: Buffer | string | Uint8Array): Buffer => (Buffer.isBuffer(u8) ? u8 : Buffer.from(u8));

// --------------------------------
// AES-256-GCM
// --------------------------------
export const aesGcmEncrypt = (plaintextBuf: Buffer, key32Buf: Buffer, aadBuf?: Buffer): AesGcmResult => {
  const key = toBuf(key32Buf);
  if (key.length !== 32) throw new Error('AES-GCM key must be 32 bytes');
  const iv = randomBytes(12);
  const cipher = createCipheriv('aes-256-gcm', key, iv, { authTagLength: 16 });
  if (aadBuf) cipher.setAAD(toBuf(aadBuf), { plaintextLength: plaintextBuf.length });
  const enc = Buffer.concat([cipher.update(toBuf(plaintextBuf)), cipher.final()]);
  const tag = cipher.getAuthTag();
  return { iv, cipher: enc, tag };
};

export const aesGcmDecrypt = (
  cipherBuf: Buffer,
  ivBuf: Buffer,
  tagBuf: Buffer,
  key32Buf: Buffer,
  aadBuf?: Buffer
): Buffer => {
  const key = toBuf(key32Buf);
  if (key.length !== 32) throw new Error('AES-GCM key must be 32 bytes');
  const iv = toBuf(ivBuf);
  const decipher = createDecipheriv('aes-256-gcm', key, iv, { authTagLength: 16 });
  if (aadBuf) decipher.setAAD(toBuf(aadBuf), { plaintextLength: cipherBuf.length });
  decipher.setAuthTag(toBuf(tagBuf));
  return Buffer.concat([decipher.update(toBuf(cipherBuf)), decipher.final()]);
};

export const encryptStringAES = (plainText: string, aadStr?: string): AesGcmResult => {
  return aesGcmEncrypt(Buffer.from(plainText, 'utf8'), KEY_32_BUFF, aadStr ? Buffer.from(aadStr) : undefined);
};

export const decryptToStringAES = (cipherBuf: Buffer, ivBuf: Buffer, tagBuf: Buffer, aadStr?: string): string => {
  const dec = aesGcmDecrypt(cipherBuf, ivBuf, tagBuf, KEY_32_BUFF, aadStr ? Buffer.from(aadStr) : undefined);
  return dec.toString('utf8');
};
